import { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Check } from 'lucide-react';
import { submitSimUserDetails } from '../../../Services/APIs/scan';
import { getClinicDetails } from '../../../Utils/clinicAuth';
import { Banner } from './ModalShell';
import { PRIMARY_BUTTON, QUIET_BUTTON, MICRO_LABEL, INPUT_CLASSES } from './constants';

const errorClasses = 'text-xs text-red-500 mt-1';

const splitName = (fullName) => {
    const parts = fullName.trim().split(/\s+/);
    return { first_name: parts[0] || '', last_name: parts.slice(1).join(' ') };
};

const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required('A patient name is required'),
    email: Yup.string()
        .trim()
        .email('Enter a valid email address')
        .required('An email address is required'),
});

const SaveStep = ({ uid, exitType, initialName, initialEmail, onSaved, onBack }) => {
    const [submitError, setSubmitError] = useState(null);

    const handleSubmit = async (values, { setSubmitting }) => {
        setSubmitError(null);

        const name = values.name.trim();
        const email = values.email.trim();

        try {
            const response = await submitSimUserDetails({
                ...splitName(name),
                email,
                company: getClinicDetails()?.company,
                patient_UUID: uid,
                is_form_completed: true,
            });
            // The created record's id is what a chat thread attaches to; tolerate either nesting.
            onSaved(response?.data?.data?._id || response?.data?._id || null, name, email);
        } catch (error) {
            setSubmitError(
                error?.response?.data?.message ||
                error?.data?.message ||
                "We couldn't save this patient. Please try again."
            );
        }

        setSubmitting(false);
    };

    return (
        <div>
            {submitError && <Banner>{submitError}</Banner>}

            <p className="text-[13px] text-[#6B6B6B] leading-5 mb-5">
                {exitType === 'share'
                    ? "Add the patient's details to save this simulation and message them."
                    : 'Add the patient details to attach this simulation to a record.'}
            </p>

            <Formik
                initialValues={{ name: initialName || '', email: initialEmail || '' }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, values }) => (
                    <Form>
                        <div className="mb-4">
                            <label htmlFor="sim-patient-name" className={`${MICRO_LABEL} block text-[#839282] mb-1.5`}>
                                Patient Name
                            </label>
                            <Field
                                id="sim-patient-name"
                                autoFocus
                                type="text"
                                name="name"
                                placeholder="Sarah Johnson"
                                className={INPUT_CLASSES}
                            />
                            <ErrorMessage name="name" component="div" className={errorClasses} />
                        </div>

                        <div className="mb-4">
                            <label htmlFor="sim-patient-email" className={`${MICRO_LABEL} block text-[#839282] mb-1.5`}>
                                Email
                            </label>
                            <Field
                                id="sim-patient-email"
                                type="email"
                                name="email"
                                placeholder="sarah@email.com"
                                className={INPUT_CLASSES}
                            />
                            <ErrorMessage name="email" component="div" className={errorClasses} />
                        </div>

                        <div className="mt-5 flex flex-col gap-2">
                            <button type="submit" disabled={isSubmitting} className={PRIMARY_BUTTON}>
                                {isSubmitting ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                ) : (
                                    <Check className="w-3.5 h-3.5" />
                                )}
                                <span className={MICRO_LABEL}>
                                    {exitType === 'share' ? 'Save & Continue' : 'Save Patient Record'}
                                </span>
                            </button>
                            {/* Hands the typed values back so they survive a trip to the result step and back. */}
                            <button type="button" onClick={() => onBack(values)} className={QUIET_BUTTON}>
                                ← Back to result
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default SaveStep;
